import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { partnersAPI } from "../utils/api";
import { Plus, Search, Building2, Users, Phone, Mail, Globe, Loader2, Trash2 } from "lucide-react";
import PartnerModal from "../components/modals/PartnerModal";

const Partners = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const fetchPartners = useCallback(async () => {
    try {
      setLoading(true);
      const response = await partnersAPI.getAll();
      setPartners(response.data);
    } catch (error) {
      console.error("Error fetching partners:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  const handleEdit = (partner) => {
    setSelectedPartner(partner);
    setShowModal(true);
  };

  const handleCreate = () => {
    setSelectedPartner(null);
    setShowModal(true);
  };

  const handleDeletePartner = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await partnersAPI.delete(deleteTarget._id);
      setDeleteTarget(null);
      fetchPartners();
    } catch (err) {
      setDeleteError(err.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const filteredPartners = useMemo(() => partners.filter(p => 
    p.partner_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.nature_of_business?.toLowerCase().includes(searchTerm.toLowerCase())
  ), [partners, searchTerm]);

  const formatCurrency = (value) => {
    return `RM ${(value || 0).toLocaleString('en-MY', { minimumFractionDigits: 0 })}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="partners-loading">
        <Loader2 className="animate-spin text-primary" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="partners-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Partners</h1>
          <p className="text-slate-500 mt-1 text-sm">Manage partner profiles and relationships</p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors self-start sm:self-auto"
          data-testid="new-partner-btn"
        >
          <Plus size={18} />
          New Partner
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
        <input
          type="text"
          placeholder="Search partners..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          data-testid="partner-search"
        />
      </div>

      {/* Partners Grid */}
      {filteredPartners.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-slate-200">
          <Building2 size={48} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-600">No partners found</p>
          <button
            onClick={handleCreate}
            className="mt-4 text-primary hover:underline"
          >
            Add your first partner
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPartners.map((partner) => (
            <div
              key={partner._id}
              className="bg-white rounded-lg border border-slate-200 p-5 hover:shadow-lg transition-shadow cursor-pointer group"
              onClick={() => handleEdit(partner)}
              data-testid={`partner-card-${partner._id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">{partner.partner_name}</h3>
                  {partner.nature_of_business && (
                    <p className="text-sm text-slate-500">{partner.nature_of_business}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    partner.active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {partner.active ? 'Active' : 'Inactive'}
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); setDeleteTarget(partner); setDeleteError(null); }}
                    className="text-red-400 hover:text-red-600 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`delete-partner-${partner._id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              {partner.products_services && (
                <div className="mb-3">
                  <p className="text-xs text-slate-500 uppercase mb-1">Products/Services</p>
                  <p className="text-sm text-slate-700 line-clamp-2">{partner.products_services}</p>
                </div>
              )}

              {/* Primary Contact */}
              {partner.contacts && partner.contacts.length > 0 && (
                <div className="mb-3 p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase mb-2">Primary Contact</p>
                  {partner.contacts.filter(c => c.is_primary).slice(0, 1).map((contact, idx) => (
                    <div key={idx}>
                      <p className="text-sm font-medium text-slate-900">{contact.name}</p>
                      {contact.designation && (
                        <p className="text-xs text-slate-500">{contact.designation}</p>
                      )}
                      <div className="flex gap-3 mt-2 text-xs text-slate-600">
                        {contact.email && (
                          <span className="flex items-center gap-1">
                            <Mail size={12} /> {contact.email}
                          </span>
                        )}
                        {contact.phone && (
                          <span className="flex items-center gap-1">
                            <Phone size={12} /> {contact.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  {!partner.contacts.some(c => c.is_primary) && partner.contacts[0] && (
                    <div>
                      <p className="text-sm font-medium text-slate-900">{partner.contacts[0].name}</p>
                      {partner.contacts[0].designation && (
                        <p className="text-xs text-slate-500">{partner.contacts[0].designation}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Stats */}
              <div className="flex justify-between items-center pt-3 border-t border-slate-100">
                <div className="text-sm">
                  <span className="text-slate-500">Deals: </span>
                  <span className="font-semibold text-slate-900">{partner.total_deals || 0}</span>
                </div>
                <div className="text-sm">
                  <span className="text-slate-500">Won: </span>
                  <span className="font-semibold text-green-600">{formatCurrency(partner.total_won_value)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Partner Modal */}
      <PartnerModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedPartner(null);
        }}
        partner={selectedPartner}
        onSuccess={fetchPartners}
      />

      {/* Delete Confirmation Dialog */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setDeleteTarget(null)} data-testid="delete-partner-dialog">
          <div className="bg-white rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Delete Partner</h3>
            <p className="text-sm text-slate-600 mb-4">Are you sure you want to delete <strong>{deleteTarget.partner_name}</strong>? This action cannot be undone.</p>
            {deleteError && <p className="text-sm text-red-600 mb-3">{deleteError}</p>}
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50">Cancel</button>
              <button onClick={handleDeletePartner} disabled={deleting} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50" data-testid="confirm-delete-partner-btn">{deleting ? 'Deleting...' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Partners;
